/**
 * Telemetry sampler for audio observability.
 *
 * Anomalies (dead-air episodes, browser-mute onset) get a per-key **min
 * interval**, never 1-in-N. These events are rare by construction, so a 1-in-N
 * sampler would drop the few we get. The interval exists to stop a *stuck* peer
 * re-firing the same episode every detector cycle; it caps the Sentry error
 * quota rather than acting as an optional dedupe. Key on
 * `(eventName, listener, peer)` so one bad pair can't mute another.
 *
 * A key may additionally opt into **backoff**, which doubles its required gap
 * after each consecutive emit up to a ceiling. A flat interval is right for a
 * key that fires a bounded number of times; it is wrong for one that fans out
 * per peer and stays hot for a whole meeting, where it costs the most quota
 * during exactly the incident the quota is for.
 *
 * A `now` clock is injectable for tests.
 */

export const DEFAULT_ANOMALY_MIN_INTERVAL_MS = 30_000
/**
 * Ceiling for a backed-off key. A persistent fault should keep reporting
 * forever — going silent would make a still-broken client look recovered — but
 * once the first few events have described it, further copies add cost and no
 * information.
 */
export const DEFAULT_MAX_ANOMALY_INTERVAL_MS = 8 * 60_000

export interface TelemetrySamplerOptions {
  /** Min gap between two anomaly emits for the same key. */
  anomalyMinIntervalMs?: number
  /** Ceiling the backoff policy grows towards. */
  maxAnomalyIntervalMs?: number
  /** Injectable clock (defaults to Date.now). */
  now?: () => number
}

export interface AnomalyGateOptions {
  /**
   * Double the required gap after each consecutive emit, up to the ceiling.
   *
   * For keys that fan out per peer and can stay hot for the length of a
   * meeting. A flat interval means a stuck client re-reports every peer at a
   * fixed rate for as long as it stays broken, which costs the most quota
   * during exactly the fleet-wide incident the quota is needed for.
   *
   * Do NOT enable this for a key a consumer debounces against a fixed cadence
   * (the recovery nudge): stretching the gap breaks the consumer's notion of
   * which events belong to the same run.
   */
  backoff?: boolean
}

/** Per-key gate bookkeeping. */
interface AnomalyEntry {
  lastEmitAt: number
  /** Consecutive emits with no quiet gap; drives the backoff exponent. */
  streak: number
}

export class TelemetrySampler {
  private readonly anomalyMinIntervalMs: number
  private readonly maxAnomalyIntervalMs: number
  private readonly now: () => number

  /** Gate state per anomaly key. */
  private readonly anomalyState = new Map<string, AnomalyEntry>()

  constructor(options: TelemetrySamplerOptions = {}) {
    this.anomalyMinIntervalMs =
      options.anomalyMinIntervalMs ?? DEFAULT_ANOMALY_MIN_INTERVAL_MS
    this.maxAnomalyIntervalMs =
      options.maxAnomalyIntervalMs ?? DEFAULT_MAX_ANOMALY_INTERVAL_MS
    this.now = options.now ?? Date.now
  }

  /**
   * Build a stable anomaly key. Pass the event name and the participant pair;
   * a missing peer is fine (whole-client anomalies).
   */
  static anomalyKey(
    eventName: string,
    listenerId: string,
    peerId?: string | null
  ): string {
    return `${eventName}:${listenerId}:${peerId ?? '*'}`
  }

  /**
   * Anomaly gate: allow if this key has not emitted within its required gap.
   * Records the emit time when it returns true, so a `true` result MUST be
   * followed by an actual emit.
   */
  allowAnomaly(key: string, options: AnomalyGateOptions = {}): boolean {
    const t = this.now()
    const entry = this.anomalyState.get(key)
    if (!entry) {
      this.anomalyState.set(key, { lastEmitAt: t, streak: 1 })
      return true
    }

    const required = options.backoff
      ? this.backoffInterval(entry.streak)
      : this.anomalyMinIntervalMs
    const elapsed = t - entry.lastEmitAt
    if (elapsed < required) return false

    // Twice the required gap with nothing emitted means the previous run
    // ended and this is a fresh incident, so it reports at the base interval
    // instead of inheriting a stretched one. Without this a client that broke
    // once early in a call would still be backed off an hour later.
    //
    // The comparison is against the *current* required gap, not the base one.
    // Comparing against the base would collapse backoff: from streak 2 on, the
    // required gap already exceeds two base intervals, so every emit would look
    // like a fresh incident and the interval would never grow past the first
    // doubling. The cost is that a client which recovers and relapses inside
    // its current gap waits out the remainder of that gap — at most `required`
    // from the last emit, and not at all once the quiet stretch exceeds it.
    entry.streak = elapsed >= required * 2 ? 1 : entry.streak + 1
    entry.lastEmitAt = t
    return true
  }

  /** Base interval doubled once per consecutive emit, capped at the ceiling. */
  private backoffInterval(streak: number): number {
    const scaled = this.anomalyMinIntervalMs * 2 ** Math.max(0, streak - 1)
    return Math.min(scaled, this.maxAnomalyIntervalMs)
  }

  /**
   * Start `key`'s interval now, as if it had just emitted. For a caller that
   * let an event past a closed gate on its own rule: the gate otherwise keeps
   * measuring from its last open, and the next ordinary event passes as soon
   * as that older interval runs out.
   */
  restartAnomaly(key: string): void {
    this.anomalyState.set(key, { lastEmitAt: this.now(), streak: 1 })
  }

  /** Drop all sampler state. */
  reset(): void {
    this.anomalyState.clear()
  }
}
